import { useState } from 'react';
import { Pressable, type PressableProps } from 'react-native';
import { YStack, type YStackProps } from 'tamagui';
import { useShadow } from '@/theme/shadow';
import { borderWidth } from '@/theme/tokens.generated';

// Card material: $card fill, hairline border, shadow-1 at rest (applied by Card
// through useShadow, so the same recipe covers web, iOS and Android). Hover and
// press never change the shadow.
export const cardStyle = {
  backgroundColor: '$card',
  borderRadius: '$lg',
  borderWidth: borderWidth.hairline,
  borderColor: '$borderColor',
} as const;

// backgroundColor deliberately uses $backgroundStrong (an opaque
// surface token, = surfaceRaised), NOT $backgroundHover -- caught in
// PR #65's own review: $backgroundHover/$backgroundPress are alpha-
// overlay tokens (rgba(13,11,8,0.04) light, meant to be LAYERED on top
// of an existing opaque background, not used as a standalone fill.
// Spreading one as this object's own backgroundColor REPLACES
// cardStyle's opaque `$card`, making the card ~96% transparent on
// hover -- it visually disappeared, showing the page through it,
// worse in dark mode where it darkened toward the page instead of
// lifting. A computed-style measurement of the resulting rgba looked
// like a normal color and didn't catch this; only actually looking at
// what it composited to would have. $backgroundStrong stays opaque and
// reads as a real "lift" in dark mode (#473e33 vs card's #332d25);
// light mode's shift is subtler ($backgroundStrong equals light's
// surfacePage) but border/shadow still carry real hover feedback there.
const hoverStyle = {
  borderColor: '$borderColorHover',
  backgroundColor: '$backgroundStrong',
} as const;

// Press is 1px of travel; no scale, no spring, no background override (the
// press token is a translucent overlay and would hollow the card out).
const pressStyle = {
  y: 1,
} as const;

// $borderColorFocus does NOT collide with $card (unlike PrimaryButton's
// fill, $card is a neutral warm off-white/near-black, not the brand
// red) -- measured 6.93:1 light / 3.17:1 dark against $card's real hex
// values, both clearing the 3:1 UI-component floor. borderWidth stays
// at cardStyle's 1 (not bumped to 2) -- a width change on focus causes
// a real layout shift (flagged in review); color alone is still a
// clearly visible change at this contrast.
const focusStyle = {
  borderColor: '$borderColorFocus',
} as const;

// Explicit react-native Pressable + React state, not a Tamagui
// styled(Button/YStack) wrapper -- tried both and reverted, verified
// live via real DOM inspection each time, not assumed:
//   - styled(YStack, {...}) with accessibilityRole="button" produces
//     NEITHER a real DOM role="button" NOR keyboard focusability on
//     web at all -- invisible to a screen reader and untabbable, a
//     real P0 violation of this project's own "Keyboard navigable"
//     rule that only DOM inspection caught (a screenshot looked fine).
//   - styled(Button, {...}) DOES get correct role/keyboard semantics,
//     but Button carries its own internal default theme that silently
//     overrode this file's custom borderColor/hoverStyle/shadow values
//     in testing (measured computed styles that didn't match anything
///    declared here) -- not worth chasing through Tamagui internals
//     when Pressable is already the proven-correct pattern this exact
//     codebase uses elsewhere (e.g. the Sign out link in profile).
// Pressable's own onHoverIn/onHoverOut/onFocus/onBlur/pressed give full
// explicit control over every state instead of relying on Tamagui
// pseudo-style merge behavior.
export function Card({ children, onPress, ...props }: YStackProps & Pick<PressableProps, 'onPress'>) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const rest = useShadow(1);

  return (
    <Pressable
      accessibilityRole={onPress ? 'button' : undefined}
      // Mirrors screen-container.tsx's own fix for the identical bug --
      // Pressable defaults accessible to true, collapsing every
      // descendant into one VoiceOver node. Correct when this IS one
      // tappable unit (onPress set), wrong for a non-interactive card
      // (the one call site today always passes onPress, so this was
      // latent, not live -- closed anyway rather than left for the
      // next call site to hit it).
      accessible={!!onPress}
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      {({ pressed }) => (
        <YStack
          {...cardStyle}
          {...rest}
          {...(hovered ? hoverStyle : {})}
          {...(pressed ? pressStyle : {})}
          {...(focused ? focusStyle : {})}
          // Issue #147 AC1: Card manually spreads its hover/press/focus
          // style bags via React state (see this file's own comment on
          // why Pressable + explicit state beats Tamagui's built-in
          // pseudo-props for this component), which meant every one of
          // those swaps applied INSTANTLY -- no `transition` prop was
          // set anywhere, so `scale` (the press feedback) and
          // backgroundColor/borderColor snapped on every state change,
          // on both platforms.
          //
          // "bouncy" (a registered preset name), NOT a raw CSS string
          // like motion.ts's own PRESS_SPRING_TRANSITION -- primary-
          // button.tsx's own comment (below its own `pressStyle`)
          // already documents why: Tamagui's TOP-LEVEL `transition`
          // prop only accepts a registered animation name or a
          // spring-config object, and crashes at runtime ("Failed to
          // set an indexed property [0] on 'CSSStyleDeclaration'") if
          // handed a raw CSS string there -- the exact class of crash
          // that caused this project's own #191 production incident
          // (see tamagui.config.ts's own history). Raw strings are only
          // safe INSIDE a pseudo-style's own `transition:` sub-key
          // (PrimaryButton's pattern), which Card doesn't have since it
          // bypasses Tamagui's pseudo-prop system entirely -- "bouncy"
          // resolves correctly at the top level on both platforms: real
          // spring physics on native, @tamagui/config's own CSS bouncy
          // preset on web.
          transition="quick"
          {...props}
        >
          {children}
        </YStack>
      )}
    </Pressable>
  );
}
